import React from 'react';
import { useIntl } from 'react-intl';
import { gql } from '@apollo/client';
import { useQuery, useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Notification } from './model';

const NOTIFICATIONS_QUERY = gql`
  query Notifications {
    notifications {
      id
      message
      url
      createdAt
    }
  }
`;

const MARK_NOTIFIED_MUTATION = gql`
  mutation MarkNotified($id: Int!) {
    markNotified(id: $id) {
      id
    }
  }
`;

const Notifications: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { data } = useQuery<{ notifications: Notification[] }>(
    NOTIFICATIONS_QUERY,
    {
      fetchPolicy: 'network-only',
      pollInterval: 60000,
    }
  );

  const [markNotifiedMutation] = useMutation(MARK_NOTIFIED_MUTATION);

  const markNotified = async (notification: Notification) => {
    const result = await markNotifiedMutation({
      variables: { id: notification.id },
      refetchQueries: [{ query: NOTIFICATIONS_QUERY }],
    });
    return notification;
  };

  const onNotificationClick = async (notification: Notification) => {
    if (notification.url) navigate(notification.url);
    await markNotified(notification);
  };

  const getTimePassed = (notification: Notification) => {
    const minutesDiff = moment().diff(notification.createdAt, 'minutes');
    const hoursDiff = moment().diff(notification.createdAt, 'hours');
    const daysDiff = moment().diff(notification.createdAt, 'days');

    let timespan: 'minutes' | 'hours' | 'days' = 'minutes';
    let duration = minutesDiff;

    if (minutesDiff >= 60) {
      timespan = hoursDiff >= 24 ? 'days' : 'hours';
      duration = timespan === 'days' ? daysDiff : hoursDiff;
    }

    return { timespan, duration };
  };

  const notifications = data?.notifications || [];

  return (
    <div>
      {notifications.length > 0 ? (
        <div className="notifications">
          {notifications.map((notification) => {
            const timePassed = getTimePassed(notification);
            return (
              <div
                onClick={() => onNotificationClick(notification)}
                className="notifications--message"
                key={notification.id}
              >
                <div>{notification.message}</div>
                <time>
                  {intl.formatMessage({ id: 'time.span.pretimespan' })}
                  {timePassed.duration}{' '}
                  {intl.formatMessage({
                    id: `time.span.${timePassed.timespan}`,
                  })}
                </time>
                <span className="notifications--message-status" />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="notifications">
          <div className="notifications--message no-messages">
            {intl.formatMessage({ id: 'notifications.none.found' })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
